/**
 * SSE (Server-Sent Events) 通用工具类
 * 参考 api.js 的配置化方式，支持多种 token 获取方式和连接配置
 */

import BasicConfig from '@/config/basic'
import Session from '@/utils/session'
import { handleLoginExpired, shouldRefreshToken } from './refreshToken'

/**
 * Token 获取方式配置
 * 支持从 Redux、Session 或自定义函数获取 token
 */
const getTokenBySource = (tokenSource, tokenPath) => {
  if (tokenSource === 'redux') {
    // 从 Redux 获取 token
    try {
      if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
        const state = window.__REDUX_STORE__.getState()
        if (tokenPath) {
          // 根据路径获取 token（如 'app.javaToken'）
          const pathParts = tokenPath.split('.')
          let value = state
          for (const part of pathParts) {
            value = value?.[part]
          }
          return value || ''
        }
        // 向后兼容：默认使用 app.javaToken
        return state?.app?.javaToken || ''
      }
    } catch (e) {
      console.warn('[SSE] 获取 Redux token 失败:', e)
    }
    return ''
  } else if (tokenSource === 'session') {
    // 从 Session 获取 token
    return Session.getToken() || ''
  }
  // 其他方式或自定义函数
  return ''
}

/**
 * SSE 配置类型
 * 参考 api.js 的 API_FORMAT_CONFIG 方式
 */
export const SSE_CONFIG = {
  // Java API SSE 配置
  java: {
    baseUrl: () => BasicConfig.jDomain,
    tokenSource: 'redux', // 从 Redux 获取 token
    tokenPath: 'app.javaToken', // Redux 路径
    // 使用 Authorization Header（原生 EventSource 不支持 header，所以会走 fetch-stream 实现）
    authIn: 'header',
    tokenHeaderName: 'Authorization',
    tokenPrefix: 'Bearer ',
    // 仍保留 query 配置（向后兼容/后端不支持 header 时可切回）
    tokenParamName: 'token', // URL 参数名
  },
  // // 萤石云 SSE 配置（示例）
  // ys7: {
  //   baseUrl: () => BasicConfig.ys7Domain,
  //   tokenSource: 'redux',
  //   tokenPath: 'app.ys7Token',
  //   tokenParamName: 'accessToken',
  // },
  // // 默认配置
  // default: {
  //   baseUrl: () => BasicConfig.domain,
  //   tokenSource: 'session',
  //   tokenPath: null,
  //   tokenParamName: 'token',
  // },
}

/**
 * SSE 协议解析：从文本流中解析 event/data/id/retry，并触发回调
 */
const parseSseStream = ({ text, dispatchEvent, defaultEventName = 'message' }) => {
  const lines = text.split(/\r?\n/)
  let eventName = defaultEventName
  let dataLines = []

  const flush = () => {
    if (dataLines.length === 0) return
    const data = dataLines.join('\n')
    dispatchEvent(eventName || defaultEventName, { data })
    dataLines = []
    eventName = defaultEventName
  }

  for (const line of lines) {
    if (!line) {
      flush()
      continue
    }
    if (line.startsWith(':')) continue // comment
    const idx = line.indexOf(':')
    const field = idx === -1 ? line : line.slice(0, idx)
    const value = idx === -1 ? '' : line.slice(idx + 1).trimStart()
    if (field === 'event') eventName = value
    if (field === 'data') dataLines.push(value)
    // id/retry 目前不需要，可按需扩展
  }
  // 外层已按“空行”分割成完整事件块，这里可以 flush 掉尾部数据
  flush()
}

/**
 * 使用 fetch + ReadableStream 实现 SSE（支持自定义 headers）
 * 返回一个“类 EventSource”对象：onopen/onerror/onmessage/addEventListener/close/readyState
 */
const createFetchEventSource = ({ url, headers = {}, onOpen, onError, onClose, onMessage, onActivity }) => {
  const listeners = new Map()
  let closeNotified = false
  const addListener = (type, cb) => {
    if (!listeners.has(type)) listeners.set(type, new Set())
    listeners.get(type).add(cb)
  }
  const notifyClose = () => {
    if (closeNotified) return
    closeNotified = true
    if (typeof onClose === 'function') {
      try { onClose() } catch (e) {}
    }
  }
  const dispatchEvent = (type, event) => {
    // 任何 SSE 事件都视为"连接还活着"，统一上报活跃状态
    // 让上层（如 reconnectManager）的心跳看门狗不会因为后端 event 名变化而误判
    if (typeof onActivity === 'function') {
      try { onActivity(type, event) } catch (e) {}
    }
    // 兼容 EventSource：默认也走 onmessage
    if (type === 'message' && typeof onMessage === 'function') {
      try { onMessage(event) } catch (e) {}
    }
    const set = listeners.get(type)
    if (set) {
      set.forEach((cb) => {
        try { cb(event) } catch (e) {}
      })
    }
  }

  const controller = new AbortController()
  const esLike = {
    readyState: 0, // CONNECTING=0, OPEN=1, CLOSED=2
    onopen: null,
    onerror: null,
    onmessage: null,
    addEventListener: (type, cb) => addListener(type, cb),
    close: () => {
      controller.abort()
      esLike.readyState = 2
      notifyClose()
    }
  }

  ;(async () => {
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...headers,
        },
        signal: controller.signal,
      })

      if (!res.ok) {
        const err = new Error(`[SSE] HTTP ${res.status}`)
        err.status = res.status
        
        // 检查是否为登录失效状态码（401/403 等）
        // 尝试从 URL 或配置中获取 axiosType
        try {
          const urlObj = new URL(url, window.location.origin)
          const configType = urlObj.searchParams.get('configType') || 'java' // 默认使用 java
          
          // 检查是否需要刷新 token
          if (shouldRefreshToken(configType, res.status)) {
            try {
              // 尝试刷新 token
              await handleLoginExpired(configType, res.status)
              // 刷新成功后，可以重新建立连接（由调用方决定）
              console.log(`[SSE] ${configType} token 刷新成功，建议重新建立连接`)
            } catch (e) {
              console.warn(`[SSE] ${configType} token 刷新失败:`, e)
            }
          }
        } catch (e) {
          // URL 解析失败，忽略
        }
        
        throw err
      }

      esLike.readyState = 1
      // 只触发一次：构造参数 onOpen 已赋给 esLike.onopen，外层还可能再包一层
      if (typeof esLike.onopen === 'function') esLike.onopen()
      else if (typeof onOpen === 'function') onOpen()

      const reader = res.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          const err = new Error('[SSE] Stream ended')
          err.code = 'SSE_STREAM_ENDED'
          throw err
        }

        // 收到任何字节流都视为"连接还活着"，统一上报活跃
        // 这覆盖了 SSE 注释行（如 `:heartbeat\n\n`）的保活场景——
        // 注释行不会触发任何 event，但仍是服务端在表明连接存活
        if (value && value.byteLength > 0 && typeof onActivity === 'function') {
          try { onActivity('raw') } catch (e) {}
        }

        buffer += decoder.decode(value, { stream: true })

        // 以空行分隔事件
        const parts = buffer.split(/\r?\n\r?\n/)
        buffer = parts.pop() || ''
        for (const part of parts) {
          parseSseStream({
            text: part,
            dispatchEvent,
            defaultEventName: 'message'
          })
        }
      }
    } catch (err) {
      // abort 不算错误
      if (controller.signal.aborted) return
      esLike.readyState = 2
      if (typeof esLike.onerror === 'function') {
        try { esLike.onerror(err) } catch (e) {}
      } else if (typeof onError === 'function') {
        try { onError(err) } catch (e) {}
      }
    }
  })()

  // 兼容 EventSource 的属性回调
  if (typeof onMessage === 'function') esLike.onmessage = onMessage
  if (typeof onOpen === 'function') esLike.onopen = onOpen
  if (typeof onError === 'function') esLike.onerror = onError

  return esLike
}

/**
 * 获取 SSE 连接 URL
 * 
 * @param {Object} options - 配置选项
 * @param {string} options.configType - 配置类型（java/ys7/default），默认为 'java'
 * @param {Object} options.params - 查询参数（可选）
 * @param {string} options.baseUrl - 自定义 baseUrl（可选，覆盖配置中的 baseUrl）
 * @param {string} options.endpoint - 自定义 endpoint（可选，覆盖配置中的 endpoint）
 * @param {Function} options.getToken - 自定义 token 获取函数（可选，覆盖配置中的 tokenSource）
 * @returns {string} SSE 连接 URL
 * 
 * @example
 * // 使用 Java 配置
 * const url = getSSEConnectUrl({ configType: 'java', params: { userId: '123' } })
 * 
 * // 使用自定义配置
 * const url = getSSEConnectUrl({
 *   baseUrl: 'https://api.example.com',
 *   endpoint: '/events',
 *   getToken: () => 'custom-token',
 *   params: { type: 'notification' }
 * })
 */
export function getSSEConnectUrl(options = {}) {
  const {
    configType = 'java',
    params = {},
    baseUrl: customBaseUrl,
    endpoint: customEndpoint,
    getToken: customGetToken,
  } = options

  // 获取配置
  const config = SSE_CONFIG[configType] || SSE_CONFIG.default

  // 确定 baseUrl
  const baseUrl = customBaseUrl || (typeof config.baseUrl === 'function' ? config.baseUrl() : config.baseUrl)
  
  // 确定 endpoint（必须通过参数传入，不再从配置中获取）
  // endpoint 应该在服务层（如 jYs7.js）中定义，保持灵活性
  if (!customEndpoint) {
    throw new Error('[SSE] endpoint 必须通过参数传入，请在服务层（如 jYs7.js）中定义 endpoint')
  }
  const endpoint = customEndpoint

  // 获取 token
  let token = ''
  if (customGetToken && typeof customGetToken === 'function') {
    token = customGetToken()
  } else {
    token = getTokenBySource(config.tokenSource, config.tokenPath)
  }

  // 构建查询参数
  const queryParams = new URLSearchParams()

  // 添加 token（如果存在）：
  // - authIn=query：token 放 query
  // - authIn=header：token 放 header，不放 query（避免泄露/缓存问题）
  if (token && config.authIn !== 'header') {
    queryParams.append(config.tokenParamName, token)
  }

  // 添加其他参数
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      queryParams.append(key, params[key])
    }
  })

  // 构建完整 URL
  const queryString = queryParams.toString()
  const url = `${baseUrl}${endpoint}${queryString ? `?${queryString}` : ''}`

  return url
}

/**
 * 创建 SSE 连接
 * 
 * @param {Object} options - 配置选项
 * @param {string} options.configType - 配置类型（java/ys7/default），默认为 'java'
 * @param {Object} options.params - 查询参数（可选）
 * @param {Function} options.onMessage - 消息处理回调函数 (event) => {}
 * @param {Function} options.onError - 错误处理回调函数 (error) => {}
 * @param {Function} options.onOpen - 连接打开回调函数 () => {}
 * @param {Function} options.onClose - 连接关闭回调函数 () => {}
 * @param {string} options.baseUrl - 自定义 baseUrl（可选）
 * @param {string} options.endpoint - 自定义 endpoint（可选）
 * @param {Function} options.getToken - 自定义 token 获取函数（可选）
 * @returns {EventSource} EventSource 实例
 * 
 * @example
 * // 使用 Java 配置创建连接
 * const eventSource = createSSEConnect({
 *   configType: 'java',
 *   params: { userId: '123' },
 *   onMessage: (event) => {
 *     console.log('收到消息:', event.data)
 *   },
 *   onError: (error) => {
 *     console.error('连接错误:', error)
 *   },
 *   onOpen: () => {
 *     console.log('连接已打开')
 *   }
 * })
 * 
 * // 使用自定义配置创建连接
 * const eventSource = createSSEConnect({
 *   baseUrl: 'https://api.example.com',
 *   endpoint: '/events',
 *   getToken: () => 'custom-token',
 *   onMessage: (event) => console.log(event.data)
 * })
 */
export function createSSEConnect(options = {}) {
  const {
    configType,
    params,
    onMessage,
    onError,
    onOpen,
    onClose,
    onActivity,
    baseUrl,
    endpoint,
    getToken,
  } = options

  // 获取 SSE 连接 URL
  const url = getSSEConnectUrl({
    configType,
    params,
    baseUrl,
    endpoint,
    getToken,
  })

  // 获取配置 & token（用于 header 鉴权）
  const cfg = SSE_CONFIG[configType] || SSE_CONFIG.default
  const token = getToken ? getToken() : getTokenBySource(cfg?.tokenSource, cfg?.tokenPath)

  // authIn=header：必须使用 fetch-stream；token 未就绪时禁止退化为无鉴权 EventSource
  if (cfg?.authIn === 'header' && !token) {
    throw new Error('[SSE] token 未就绪，无法建立连接')
  }
  const useHeaderAuth = cfg?.authIn === 'header' && token
  const eventSource = useHeaderAuth
    ? createFetchEventSource({
      url,
      headers: {
        [cfg.tokenHeaderName || 'Authorization']: `${cfg.tokenPrefix || 'Bearer '}${token}`
      },
      onMessage,
      onError,
      onOpen,
      onClose,
      onActivity
    })
    : new EventSource(url)

  // 绑定消息处理
  if (onMessage && typeof onMessage === 'function') {
    eventSource.onmessage = (event) => {
      try {
        onMessage(event)
      } catch (error) {
        console.error('[SSE] 消息处理回调函数执行失败:', error)
      }
    }
  } else {
    // 默认消息处理
    eventSource.onmessage = (event) => {
      // console.log('[SSE] 收到消息:', event.data)
    }
  }

  // 绑定错误处理
  if (onError && typeof onError === 'function') {
    eventSource.onerror = (error) => {
      try {
        onError(error)
      } catch (err) {
        console.error('[SSE] 错误处理回调函数执行失败:', err)
      }
    }
  } else {
    // 默认错误处理
    eventSource.onerror = (error) => {
      console.error('[SSE] 连接错误:', error)
      // 如果连接关闭，尝试重新连接
      if (eventSource.readyState === EventSource.CLOSED) {
        console.warn('[SSE] 连接已关闭，需要手动重新连接')
      }
    }
  }

  // 绑定连接打开处理
  if (onOpen && typeof onOpen === 'function') {
    eventSource.onopen = () => {
      try {
        onOpen()
      } catch (error) {
        console.error('[SSE] 连接打开回调函数执行失败:', error)
      }
    }
  } else {
    // 默认连接打开处理
    eventSource.onopen = () => {
      // console.log('[SSE] 连接已打开')
    }
  }

  // fetch-stream 版本的 close 已在内部处理 onClose，避免外层再次包装导致重复触发
  // 原生 EventSource 没有 onClose 语义，这里仅为原生实现补一层 close 回调
  if (!useHeaderAuth) {
    const originalClose = eventSource.close.bind(eventSource)
    eventSource.close = function() {
      if (onClose && typeof onClose === 'function') {
        try {
          onClose()
        } catch (error) {
          console.error('[SSE] 连接关闭回调函数执行失败:', error)
        }
      }
      originalClose()
    }
  }

  return eventSource
}

/**
 * SSE 连接管理器
 * 用于管理多个 SSE 连接，支持不同的配置类型
 * 
 * @example
 * const manager = new SSEManager()
 * 
 * // 创建 Java SSE 连接
 * manager.createConnection('java-connection', {
 *   configType: 'java',
 *   params: { userId: '123' },
 *   onMessage: (event) => console.log('Java:', event.data)
 * })
 * 
 * // 创建自定义 SSE 连接
 * manager.createConnection('custom-connection', {
 *   baseUrl: 'https://api.example.com',
 *   endpoint: '/events',
 *   onMessage: (event) => console.log('Custom:', event.data)
 * })
 * 
 * // 关闭指定连接
 * manager.closeConnection('java-connection')
 * 
 * // 关闭所有连接
 * manager.closeAllConnections()
 */
export class SSEManager {
  constructor() {
    this.connections = new Map() // 存储所有连接，key 为连接 ID
  }

  /**
   * 创建并管理 SSE 连接
   * 
   * @param {string} connectionId - 连接唯一标识
   * @param {Object} options - 连接选项（传递给 createSSEConnect）
   * @returns {EventSource} EventSource 实例
   */
  createConnection(connectionId, options = {}) {
    // 如果连接已存在，先关闭旧连接
    if (this.connections.has(connectionId)) {
      this.closeConnection(connectionId)
    }

    // 创建新连接
    const eventSource = createSSEConnect({
      ...options,
      onClose: () => {
        // 从管理器中移除连接
        this.connections.delete(connectionId)
        // 调用用户提供的 onClose 回调
        if (options.onClose && typeof options.onClose === 'function') {
          options.onClose()
        }
      }
    })

    // 保存连接
    this.connections.set(connectionId, eventSource)

    return eventSource
  }

  /**
   * 关闭指定连接
   * 
   * @param {string} connectionId - 连接唯一标识
   */
  closeConnection(connectionId) {
    const eventSource = this.connections.get(connectionId)
    if (eventSource) {
      eventSource.close()
      this.connections.delete(connectionId)
    }
  }

  /**
   * 关闭所有连接
   */
  closeAllConnections() {
    this.connections.forEach((eventSource, connectionId) => {
      eventSource.close()
    })
    this.connections.clear()
  }

  /**
   * 获取指定连接
   * 
   * @param {string} connectionId - 连接唯一标识
   * @returns {EventSource|null} EventSource 实例或 null
   */
  getConnection(connectionId) {
    return this.connections.get(connectionId) || null
  }

  /**
   * 获取所有连接 ID
   * 
   * @returns {Array<string>} 连接 ID 数组
   */
  getAllConnectionIds() {
    return Array.from(this.connections.keys())
  }

  /**
   * 检查连接是否存在
   * 
   * @param {string} connectionId - 连接唯一标识
   * @returns {boolean} 连接是否存在
   */
  hasConnection(connectionId) {
    return this.connections.has(connectionId)
  }
}

// 导出单例管理器（可选使用）
export const sseManager = new SSEManager()
